import type { ExerciseTrend } from "@training/shared";
import { dropLoadOf, firstRepTarget } from "./session.ts";

/**
 * A curve on its own is drawn in the colour of the session on screen, plain
 * light where there is none. Load and reps each have their chart, so they
 * need no colour to be told apart.
 */
export const CURVE_COLOR = "var(--hue-a)";
/**
 * A one-sided movement is read side against side, so there colour says the
 * side — the same two wherever a side is shown, the set's entry included.
 * Checked on the card surface for normal and colour-blind vision; the
 * theme's own amber came out too light to sit next to the blue.
 */
export const SIDE_COLOR = { left: "#1fa6c9", right: "#bd8712" } as const;

/** Slots a missed week takes on the axis: about one session's worth of sets. */
const MISSED_WIDTH = 2;

/**
 * One chart point per set, in the order they were done, session after
 * session. A week with no session gets slots of its own, empty: the lines
 * break there instead of joining two sessions as if nothing was skipped.
 */
export type SetPoint =
  | ({ key: string; kind: "set"; date: string; setIndex: number; load: number } & Figures)
  | ({ key: string; kind: "missed"; load: null } & { [Figure in keyof Figures]: null });

/** What a set may carry beyond its load, each null when it does not. */
interface Figures {
  /** The reps asked that day, to read the reps done against. */
  target: number | null;
  /** The left side's when the set has a right one. */
  reps: number | null;
  /** The right side of a one-sided set: its reps, and its load even when it is the left's. */
  repsRight: number | null;
  loadRight: number | null;
  /** The second half of a drop set, side by side like the first. */
  dropLoad: number | null;
  dropLoadRight: number | null;
  dropReps: number | null;
  dropRepsRight: number | null;
}

const NOTHING: { [Figure in keyof Figures]: null } = {
  target: null,
  reps: null,
  repsRight: null,
  loadRight: null,
  dropLoad: null,
  dropLoadRight: null,
  dropReps: null,
  dropRepsRight: null,
};

export interface SetChart {
  points: SetPoint[];
  /**
   * Each session, from its first set — where its date goes on the axis — to
   * its last.
   */
  sessionStarts: { key: string; last: string; date: string }[];
  /**
   * The hatched band of each run of skipped weeks, from the last set before
   * to the first one after (or the last empty slot, when nothing came after).
   */
  missed: { from: string; to: string }[];
  /** False when every day comes from the sheet, which keeps no reps. */
  hasReps: boolean;
  /** Some set was logged a side at a time: each side gets its curves. */
  hasSides: boolean;
  /** Some set was a drop set: its second load hangs under the first. */
  hasDrops: boolean;
  /** Some day with reps had a number of them asked: there is a line to be above. */
  hasTarget: boolean;
}

/** Any Monday: weeks run Monday to Sunday, the cycle's like any other (`weekOfCycle` on the server). */
const A_MONDAY = "2026-01-05";

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/**
 * Weeks with no session between two dates, both excluded. Counting days
 * instead missed the leg day of the second week: done on a Sunday, then on
 * the Tuesday nine days later — a whole week skipped in between.
 */
export function missedWeeks(from: string, to: string): number {
  const week = (date: string) => Math.floor(daysBetween(A_MONDAY, date) / 7);
  return Math.max(0, week(to) - week(from) - 1);
}

/**
 * `today` closes the chart: a week gone by since the last session counts too,
 * the current one only once it is over.
 */
export function setChart(sessions: ExerciseTrend["sessions"], today: string): SetChart {
  const points: SetPoint[] = [];
  const sessionStarts: SetChart["sessionStarts"] = [];
  const missed: SetChart["missed"] = [];
  let hasReps = false;
  let hasSides = false;
  let hasDrops = false;
  let hasTarget = false;
  let last: string | null = null;

  // Index of the empty run's first slot, closed once the next set lands.
  let open: number | null = null;
  const skip = (after: string, weeks: number) => {
    if (weeks === 0) return;
    open = points.length;
    for (let slot = 0; slot < weeks * MISSED_WIDTH; slot++) {
      points.push({ key: `missed-${after}-${slot}`, kind: "missed", load: null, ...NOTHING });
    }
  };
  const close = (to: string) => {
    if (open === null) return;
    missed.push({ from: points[open - 1]!.key, to });
    open = null;
  };

  for (const day of sessions) {
    if (day.sets.length === 0) continue;
    if (last) skip(last, missedWeeks(last, day.date));
    // The sheet's days have no reps to hold against it.
    const target = day.source === "app" ? firstRepTarget(day.targetReps) : null;
    if (target !== null) hasTarget = true;
    for (const set of day.sets) {
      const key = `${day.date}#${set.setIndex}`;
      if (set === day.sets[0]) {
        sessionStarts.push({ key, last: `${day.date}#${day.sets.at(-1)!.setIndex}`, date: day.date });
        close(key);
      }
      const sided = set.repsRight !== null || set.weightKgRight !== null;
      const dropLoad = dropLoadOf(set);
      if (set.reps !== null) hasReps = true;
      if (sided) hasSides = true;
      if (dropLoad !== null) hasDrops = true;
      points.push({
        key,
        kind: "set",
        date: day.date,
        setIndex: set.setIndex,
        load: set.weightKg,
        target,
        reps: set.reps,
        repsRight: set.repsRight,
        loadRight: sided ? (set.weightKgRight ?? set.weightKg) : null,
        dropLoad,
        dropLoadRight: sided && dropLoad !== null ? (set.dropWeightKgRight ?? dropLoad) : null,
        dropReps: dropLoad !== null ? set.dropReps : null,
        dropRepsRight: dropLoad !== null ? set.dropRepsRight : null,
      });
    }
    last = day.date;
  }
  if (last) skip(last, missedWeeks(last, today));
  close(points.at(-1)?.key ?? "");

  return { points, sessionStarts, missed, hasReps, hasSides, hasDrops, hasTarget };
}
